import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BarChart3, Check, LayoutPanelTop, X } from 'lucide-react';
export const Route = createFileRoute('/_layout/compare/heylink/')({
    component: CompareHeylinkPage,
    head: () => ({
        meta: [
            { title: 'Adora vs HeyLink — Offline Campaign Analytics vs Link Grouping' },
            {
                name: 'description',
                content: 'HeyLink focuses on link presentation. Adora focuses on campaign insight and offline-to-online performance. Compare features and decide which fits your workflow.',
            },
            { property: 'og:title', content: 'Adora vs HeyLink — Offline Campaign Analytics vs Link Grouping' },
            {
                property: 'og:description',
                content: 'HeyLink focuses on link presentation. Adora focuses on campaign insight and offline-to-online performance. Compare features and decide which fits your workflow.',
            },
        ],
    }),
});
function CompareHeylinkPage() {
    return (<main className="min-h-dvh bg-background">
            {/* Hero */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-12 md:py-16">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Compare tools</p>

                <h1 className="mt-2 text-3xl md:text-5xl font-bold">Adora vs HeyLink</h1>

                <p className="mt-4 max-w-2xl text-muted-foreground">
                    Both Adora and HeyLink let you share multiple destinations through a single link — but they are
                    built for very different outcomes.
                </p>

                <p className="mt-3 max-w-2xl text-muted-foreground">
                    <strong>TL;DR:</strong> HeyLink focuses on link presentation. Adora focuses on campaign insight and
                    offline-to-online performance.
                </p>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <Button asChild size="lg">
                        <Link to="/sign-up" search={{ redirect: '/console' }}>
                            Try Adora for free
                        </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg">
                        <Link to="/what-is-adora">What is Adora?</Link>
                    </Button>
                </div>
            </section>

            <Separator />

            {/* Core positioning */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <div className="grid gap-6 md:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <LayoutPanelTop className="h-5 w-5 text-primary"/>
                                HeyLink positioning
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>
                                HeyLink is built around <strong>link-in-bio style landing pages</strong>. It helps users
                                present multiple destinations in one place.
                            </p>
                            <ul className="mt-2 space-y-1">
                                <li>• Social media profiles</li>
                                <li>• Simple flyers or digital touchpoints</li>
                                <li>• Convenience-focused link grouping</li>
                            </ul>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <BarChart3 className="h-5 w-5 text-primary"/>
                                Adora positioning
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>
                                Adora is built around <strong>campaign analytics</strong>. It measures how people enter,
                                move, and convert across destinations.
                            </p>
                            <ul className="mt-2 space-y-1">
                                <li>• Offline and digital entry points</li>
                                <li>• Smart Links and QR codes</li>
                                <li>• Performance-driven optimization</li>
                            </ul>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Feature comparison */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <h2 className="text-2xl md:text-3xl font-semibold">Feature comparison</h2>

                <div className="mt-6 overflow-x-auto">
                    <table className="w-full border text-sm">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="px-4 py-3 text-left">Capability</th>
                                <th className="px-4 py-3 text-left">Adora</th>
                                <th className="px-4 py-3 text-left">HeyLink</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            <Row label="Link-in-bio style page" heylink/>
                            <Row label="Campaign-based tracking" adora/>
                            <Row label="Offline → online analytics" adora/>
                            <Row label="Smart Links as entry points" adora/>
                            <Row label="Group multiple destinations" adora heylink/>
                            <Row label="Compare placements & ideas" adora/>
                            <Row label="Presentation-first experience" heylink/>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Decision helper */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-10">
                <h2 className="text-2xl md:text-3xl font-semibold">Which one should you choose?</h2>

                <div className="mt-6 grid gap-6 md:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle>Choose HeyLink if…</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>• You want a simple link-in-bio page</p>
                            <p>• Presentation matters more than analytics</p>
                            <p>• You don’t need campaign-level insights</p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Choose Adora if…</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground space-y-2">
                            <p>• You run campaigns across offline & digital channels</p>
                            <p>• You want to understand where traffic really comes from</p>
                            <p>• You improve performance over time</p>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-6xl px-6 md:px-8 py-12">
                <Card>
                    <CardContent className="p-6 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div>
                            <h3 className="text-xl md:text-2xl font-semibold">
                                Built for insight, not just presentation
                            </h3>
                            <p className="mt-2 text-muted-foreground max-w-xl text-sm md:text-base">
                                If grouping links is only the start — and understanding performance matters — Adora
                                gives you visibility HeyLink was not designed for.
                            </p>
                        </div>
                        <Button asChild size="lg">
                            <Link to="/sign-up" search={{ redirect: '/console' }}>
                                Start with Adora
                            </Link>
                        </Button>
                    </CardContent>
                </Card>
            </section>
        </main>);
}
function Row({ label, adora, heylink }) {
    return (<tr>
            <td className="px-4 py-3">{label}</td>
            <td className="px-4 py-3">
                {adora ? <Check className="h-4 w-4 text-green-600"/> : <X className="h-4 w-4 text-muted-foreground"/>}
            </td>
            <td className="px-4 py-3">
                {heylink ? (<Check className="h-4 w-4 text-green-600"/>) : (<X className="h-4 w-4 text-muted-foreground"/>)}
            </td>
        </tr>);
}
